import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { HugeiconsIcon } from '@hugeicons/react';
import { UserGroupIcon } from '@hugeicons/core-free-icons';
import { useAppSession } from '../../hooks/useAppSession';
import { useTeamMembers } from '../../hooks/useTeams';
import { useUpdateProject } from '../../hooks/useProjects';
import { hiddenFromTeammates, type VisibilityPromptCandidate } from '../../lib/projectVisibilityPrompt';
import { capture } from '@/lib/analytics';
import { log } from '@/lib/logger';

interface ProjectsVisibilityBannerProps {
  teamId: string;
  projects: VisibilityPromptCandidate[] | undefined;
}

/** Per owner, per browser: the same shape as useInstallReminderDismissal. */
const dismissalKey = (userId: string) => `oqa:projects-visibility-prompt-dismissed:${userId}`;

function readDismissed(key: string | null): boolean {
  if (!key) return false;
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}

/**
 * Projects-page prompt for an owner whose private, unshared projects are
 * invisible to teammates (P0 2026-10-15, option B). Measured on production
 * that day: 3 of the 6 teams with 2+ members (one paying) held every project
 * private, so the teammate's Projects page read "No projects yet". New
 * projects are team-visible since the same change (server
 * lib/projectVisibility.ts); this prompt is what opens the EXISTING ones, on
 * the owner's next visit. "Make visible to team" PATCHes each listed project
 * through the owner-only visibility path; "Keep private" dismisses for this
 * owner. Nothing is written without a click.
 */
export function ProjectsVisibilityBanner({ teamId, projects }: ProjectsVisibilityBannerProps) {
  const { data: session } = useAppSession();
  const { data: members } = useTeamMembers(teamId);
  const updateProject = useUpdateProject();
  const key = session?.user?.id ? dismissalKey(session.user.id) : null;
  const [dismissed, setDismissed] = useState(() => readDismissed(key));
  const [busy, setBusy] = useState(false);
  const shownRef = useRef<string | null>(null);

  // The key lands after the first render (session loads async); re-read it.
  useEffect(() => { setDismissed(readDismissed(key)); }, [key]);

  const teamMemberCount = members?.length ?? 0;
  const hidden = hiddenFromTeammates(projects, teamMemberCount);
  const visible = !dismissed && hidden.length > 0;

  useEffect(() => {
    if (!visible || shownRef.current === key) return;
    shownRef.current = key;
    capture('projects_visibility_prompt_shown', { hidden_count: hidden.length, team_members: teamMemberCount });
    log.info('[ProjectsVisibilityBanner] shown', { hiddenCount: hidden.length, teamMemberCount });
  }, [visible, hidden.length, teamMemberCount, key]);

  const makeVisible = useCallback(async () => {
    setBusy(true);
    const results = await Promise.allSettled(hidden.map((p) => updateProject.mutateAsync({ id: p.id, data: { visibility: 'team' } })));
    const failed = results.filter((r) => r.status === 'rejected');
    setBusy(false);
    capture('projects_visibility_prompt_accepted', { hidden_count: hidden.length, failed_count: failed.length });
    if (failed.length > 0) {
      const first = failed[0] as PromiseRejectedResult;
      log.error('[ProjectsVisibilityBanner] visibility update refused', { failed: failed.length, of: hidden.length, error: first.reason instanceof Error ? first.reason.message : String(first.reason) });
      toast.error(`Could not update ${failed.length} of ${hidden.length} projects. Try again.`);
    }
  }, [hidden, updateProject]);

  const keepPrivate = useCallback(() => {
    if (key) {
      try { localStorage.setItem(key, '1'); } catch {
        // Storage refused (private window, quota): the prompt still hides for this app session.
      }
    }
    setDismissed(true);
    capture('projects_visibility_prompt_dismissed', { hidden_count: hidden.length });
  }, [key, hidden.length]);

  if (!visible) return null;
  const n = hidden.length;
  return (
    <section
      role="status"
      aria-label="Projects hidden from teammates"
      className="mb-4 flex flex-col gap-3 rounded-lg border border-gray-200 bg-primary-50 px-4 py-3 md:flex-row md:items-center"
    >
      <p className="flex flex-1 min-w-0 items-center gap-2 text-sm text-gray-700">
        <HugeiconsIcon icon={UserGroupIcon} size={16} strokeWidth={2} color="currentColor" className="shrink-0 text-primary-600" aria-hidden="true" />
        <span>
          <strong className="font-medium text-gray-900">{n} of your projects {n === 1 ? 'is' : 'are'} hidden from your teammates.</strong>
        </span>
      </p>
      <div className="flex shrink-0 flex-wrap gap-2">
        <button type="button" className="btn-primary btn-sm" onClick={() => void makeVisible()} disabled={busy}>
          <span className="text-cap-trim">Make visible to team</span>
        </button>
        <button type="button" className="btn-ghost btn-sm text-gray-700" onClick={keepPrivate} disabled={busy}>
          <span className="text-cap-trim">Keep private</span>
        </button>
      </div>
    </section>
  );
}
